import { Button } from "@omni/ui/components/Button/button.component";
import { RichTextContent } from "@omni/ui/components/RichTextContent/richTextContent.component";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";

import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

import { InfoBlockAnchor } from "../../components/infoBlockAnchor.component";
import { InfoBlockContent } from "../../components/infoBlockContent.component";
import { InfoBlockHeading } from "../../components/infoBlockHeading.component";
import { InfoBlockWrapper } from "../../components/infoBlockWrapper.component";

type DescriptionProps = {
    description: string;
};

const Description = ({ description }: DescriptionProps) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const [showLessButton, setShowLessButton] = useState(false);
    const [showTruncation, setShowTruncation] = useState(false);
    const [showMobileMore, setShowMobileMore] = useState(true);
    const textWrapper = useRef<HTMLDivElement>(null);
    const dotRef = useRef<any>(null);

    useEffect(() => {
        if (isClientSide) {
            import("dotdotdot-js").then((mod) => {
                if (textWrapper?.current) {
                    dotRef.current = new mod.default(textWrapper.current, {
                        callback: (isTruncated: boolean) => {
                            if (isTruncated) {
                                setShowTruncation(true);
                            }
                        },
                        height: 150,
                        ellipsis: "\u2026 ",
                        truncate: "word",
                        watch: "window"
                    });
                }
            });
        }
    }, [isClientSide]);

    useEffect(() => {
        if (!showTruncation) return;
        if (showLessButton) {
            dotRef.current?.restore();
        } else {
            dotRef.current?.truncate();
            dotRef.current?.watch();
        }
    }, [showLessButton, showTruncation]);

    return (
        <InfoBlockWrapper showMobileMore={showMobileMore}>
            <InfoBlockHeading
                title={t("productDetail.anchor.title.description")}
                onClick={() => setShowMobileMore((state) => !state)}
            />
            <InfoBlockContent>
                <Row>
                    <Col>
                        <InfoBlockAnchor
                            id={t("productDetail.anchor.hash.description")}
                        />
                        <h4
                            className={cn(
                                "tw:mb-4 tw:font-bold anchor--pdp-info-nav"
                            )}
                        >
                            <span>
                                {t("productDetail.anchor.title.description")}
                            </span>
                        </h4>

                        <div className={cn("tw:mb-4")}>
                            <RichTextContent
                                className={cn(
                                    "tw:h-auto tw:max-h-37500 tw:overflow-hidden tw:transition-all tw:duration-500 tw:ease-in-out tw:wrap-break-word"
                                )}
                                ref={textWrapper}
                                text={`<p>${description}</p>`}
                            />
                            <div>
                                {showTruncation && (
                                    <Button
                                        type="button"
                                        color="link"
                                        className={cn("tw:p-0")}
                                        onClick={() =>
                                            setShowLessButton((state) => !state)
                                        }
                                    >
                                        {showLessButton
                                            ? t(
                                                  "productDetail.description.link.showLess"
                                              )
                                            : t(
                                                  "productDetail.description.link.showMore"
                                              )}
                                        <i
                                            className={cn(
                                                `las la-angle-${
                                                    showLessButton
                                                        ? "up"
                                                        : "down"
                                                }`,
                                                "tw:inline-block! tw:ml-1"
                                            )}
                                        ></i>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </InfoBlockContent>
        </InfoBlockWrapper>
    );
};

export { Description };
